import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Divider,
  Grid,
  ModalCloseButton,
  ModalProps,
} from '@chakra-ui/react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { noop } from 'lodash';
import { ReactElement, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { getI18n } from 'react-i18next';

import { ButtonSpinner } from '../LoadingSpinner';

interface IAlertDialog extends ModalProps {
  onFormSuccess?: (data?: any) => any;
  onMutate?: () => any;
  schema: any;
  defaultValues?: any;
  content: {
    formTestId?: string;
    header?: string | ReactElement;
    formFields: (data: object) => ReactElement;
    rest?: object;
    footer?: {
      buttons: {
        formButtonTestId?: string;
        hide?: boolean;
        cancelCaption?: string;
        actionCaption?: string;
      };
    };
  };
  styleConfig?: {
    minW?: string;
    maxW?: string;
    motionPreset?: any;
    w?: string;
  };
}

export default NiceModal.create((props: IAlertDialog) => {
  const modal = useModal();

  const onClose = () => {
    modal.hide();
    modal.remove();
  };

  const { content, styleConfig = {}, onFormSuccess = onClose, onMutate = noop, schema, defaultValues = {} } = props;

  const { formFields, rest = {}, header = false, footer = { buttons: {} }, formTestId = 'form-in-modal' } = content;

  const {
    buttons: {
      hide: hideButtons = false,
      cancelCaption = false,
      actionCaption = getI18n().t('common:submit'),
      formButtonTestId = 'form-in-modal-button',
    },
  } = footer;

  const { w = 'full', maxW = '50rem', minW, motionPreset = 'slideInBottom' } = styleConfig;

  const cancelRef = useRef<HTMLButtonElement>(null);

  const {
    handleSubmit,
    formState: { errors, isValid, submitCount, isDirty },
    control,
    register,
    setError,
    setValue,
    watch,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (data) => {
      return onMutate(data);
    },
  });

  let watchedFields = watch();
  let statusWatch = watch('status');

  useEffect(() => {
    setValue('status_reason', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusWatch]);

  const onSubmit = (data: any) => {
    mutate(data, {
      // @ts-ignore
      onSuccess: (response: any) => {
        if (response) {
          const { data } = response;
          onFormSuccess(data);
        } else {
          onFormSuccess();
        }
        onClose();
      },
      onError: (error: any) => {
        const { errors, message } = error.response.data;

        setError('file', {
          type: 'server',
          message: message ?? errors['file']!.join('. '),
        });
      },
    });
  };

  return (
    <AlertDialog isOpen={modal.visible} onClose={onClose} leastDestructiveRef={cancelRef} motionPreset={motionPreset}>
      <AlertDialogOverlay sx={{ backdropFilter: 'blur(2rem)' }}>
        <AlertDialogContent minW={minW} w={w} maxW={maxW} as="form" onSubmit={handleSubmit(onSubmit)} data-test-id={formTestId}>
          {header && <AlertDialogHeader>{header}</AlertDialogHeader>}
          <ModalCloseButton data-test-id="alert-dialog-button-close" />
          {header && <Divider />}

          <AlertDialogBody>
            {formFields({
              control,
              register,
              errors,
              isPending,
              onSubmit: (val: any) => {
                onMutate(val);
                onClose();
              },
              watchedFields,
              watch,
              isDirty,
              ...rest,
            })}
          </AlertDialogBody>

          {!hideButtons ? (
            <AlertDialogFooter>
              <Grid templateColumns={`repeat(${cancelCaption ? '2' : '1'}, 1fr)`} gap={3}>
                {cancelCaption && (
                  <Button
                    variant={'outline'}
                    data-test-id="alert-dialog-cancel-button"
                    ref={cancelRef}
                    onClick={() => {
                      onClose();
                    }}
                    disabled={isPending}
                  >
                    {cancelCaption}
                  </Button>
                )}
                <Button type="submit" variant="primary" disabled={submitCount! > 0 && !isValid} data-test-id={formButtonTestId}>
                  {actionCaption} {isPending ? <ButtonSpinner /> : null}
                </Button>
              </Grid>
            </AlertDialogFooter>
          ) : null}
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
});
